import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import RichTextEditor from '../../Components/RichTextEditor/RichTextEditor';
import SEO from '../../Components/SEO/SEO';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const BlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('content');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    featuredImage: '',
    featuredImageAlt: '',
    categories: [],
    tags: [],
    status: 'draft',
    scheduledFor: '',
    contentType: 'blog',
    seo: {
      metaTitle: '',
      metaDescription: '',
      focusKeyword: '',
    },
  });

  const [tagInput, setTagInput] = useState('');
  const [categoryInput, setCategoryInput] = useState('');

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      } else {
        setError('You must be logged in to edit posts');
        navigate('/blog');
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setError('Authentication error');
    }
  }, [navigate]);

  const toDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
  };

  const fetchPost = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/blog/${id}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Post not found');
        }
        throw new Error('Failed to fetch post');
      }

      const data = await response.json();
      const post = data.post;

      // Populate form with existing data
      setFormData({
        title: post.title || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        featuredImage: post.featuredImage || '',
        featuredImageAlt: post.featuredImageAlt || '',
        categories: post.categories || [],
        tags: post.tags || [],
        status: post.status || 'draft',
        scheduledFor: toDateTimeLocal(post.scheduledFor),
        contentType: post.contentType || 'blog',
        seo: {
          metaTitle: post.seo?.metaTitle || '',
          metaDescription: post.seo?.metaDescription || '',
          focusKeyword: post.seo?.focusKeyword || '',
        },
      });

      setError(null);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    checkAuth();
    fetchPost();
  }, [checkAuth, fetchPost]);

  const handleSubmit = async (e, status = formData.status) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const nextStatus = status || formData.status;
      if (nextStatus === 'scheduled' && !formData.scheduledFor) {
        throw new Error('Please select a publish date for scheduled posts.');
      }

      const response = await fetch(`${API_URL}/blog/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          status: nextStatus,
          scheduledFor: nextStatus === 'scheduled' && formData.scheduledFor
            ? new Date(formData.scheduledFor).toISOString()
            : null,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update post');
      }

      const data = await response.json();
      // Navigate to the post detail page
      navigate(`/blog/${data.slug || id}`);
    } catch (err) {
      console.error('Error updating post:', err);
      setError(err.message);
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/blog/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete post');
      }

      navigate('/blog');
    } catch (err) {
      console.error('Error deleting post:', err);
      setError(err.message);
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter((tag) => tag !== tagToRemove),
    });
  };

  const addCategory = () => {
    if (categoryInput.trim() && !formData.categories.includes(categoryInput.trim())) {
      setFormData({
        ...formData,
        categories: [...formData.categories, categoryInput.trim()],
      });
      setCategoryInput('');
    }
  };

  const removeCategory = (catToRemove) => {
    setFormData({
      ...formData,
      categories: formData.categories.filter((cat) => cat !== catToRemove),
    });
  };

  const autoGenerateExcerpt = () => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formData.content;
    const text = tempDiv.textContent || tempDiv.innerText || '';
    const excerpt = text.substring(0, 200).trim() + '...';
    setFormData({ ...formData, excerpt });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading post...</p>
        </div>
      </div>
    );
  }

  if (error && !formData.title) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center">
          <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
            Error Loading Post
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/blog')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                     font-medium transition-colors"
          >
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO title={`Edit: ${formData.title}`} description="Edit blog post" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
          {/* Header */}
          <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Edit Post
            </h1>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mx-8 mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200
                          dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Tabs */}
          <div className="px-8 pt-6">
            <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
              {['content', 'settings', 'seo'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-medium capitalize transition-colors
                    ${activeTab === tab
                      ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={(e) => handleSubmit(e)} className="p-8">
            {/* Content Tab */}
            {activeTab === 'content' && (
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             text-2xl font-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your post title..."
                  />
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Content *
                  </label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => setFormData({ ...formData, content })}
                    placeholder="Write your blog post content..."
                  />
                </div>

                {/* Excerpt */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Excerpt
                    </label>
                    <button
                      type="button"
                      onClick={autoGenerateExcerpt}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      Auto-generate
                    </button>
                  </div>
                  <textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Brief summary of your post (optional, will be auto-generated if empty)"
                  />
                </div>

                {/* Featured Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Featured Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.featuredImage}
                    onChange={(e) => setFormData({ ...formData, featuredImage: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://example.com/image.jpg"
                  />
                  {formData.featuredImage && (
                    <img
                      src={formData.featuredImage}
                      alt="Preview"
                      className="mt-4 max-w-sm rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6">
                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Post Type
                  </label>
                  <select
                    value={formData.contentType}
                    onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="blog">Blog Post</option>
                    <option value="article">Article</option>
                  </select>
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Post Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Publish Now</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                {formData.status === 'scheduled' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Publish Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduledFor}
                      onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                               rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                               focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Scheduled posts will publish automatically based on this time.
                    </p>
                  </div>
                )}

                {/* Categories */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Categories
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={categoryInput}
                      onChange={(e) => setCategoryInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600
                               rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                               focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add a category..."
                    />
                    <button
                      type="button"
                      onClick={addCategory}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                               transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.categories.map((cat, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800
                                 dark:text-blue-200 rounded-full flex items-center gap-2"
                      >
                        {cat}
                        <button
                          type="button"
                          onClick={() => removeCategory(cat)}
                          className="text-blue-600 dark:text-blue-300 hover:text-blue-800
                                   dark:hover:text-blue-100"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600
                               rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                               focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Add a tag..."
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                               transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700
                                 dark:text-gray-300 rounded-full flex items-center gap-2"
                      >
                        #{tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900
                                   dark:hover:text-gray-100"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* SEO Tab */}
            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    value={formData.seo.metaTitle}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        seo: { ...formData.seo, metaTitle: e.target.value },
                      })
                    }
                    maxLength="60"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty to use post title"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {formData.seo.metaTitle.length}/60 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Meta Description
                  </label>
                  <textarea
                    value={formData.seo.metaDescription}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        seo: { ...formData.seo, metaDescription: e.target.value },
                      })
                    }
                    maxLength="160"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Leave empty to use excerpt"
                  />
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    {formData.seo.metaDescription.length}/160 characters
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Focus Keyword
                  </label>
                  <input
                    type="text"
                    value={formData.seo.focusKeyword}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        seo: { ...formData.seo, focusKeyword: e.target.value },
                      })
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600
                             rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., blockchain, web3, LUKSO"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                disabled={saving || !formData.title || !formData.content}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                         font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Update Post'}
              </button>

              {formData.status === 'scheduled' && (
                <button
                  type="button"
                  onClick={() => handleSubmit(new Event('submit'), 'scheduled')}
                  disabled={saving || !formData.title || !formData.content || !formData.scheduledFor}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg
                         font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Scheduling...' : 'Schedule Post'}
                </button>
              )}

              <button
                type="button"
                onClick={() => handleSubmit(new Event('submit'), 'published')}
                disabled={saving || !formData.title || !formData.content}
                className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg
                         font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Publishing...' : 'Publish Now'}
              </button>

              <button
                type="button"
                onClick={() => navigate(`/blog/${id}`)}
                disabled={saving}
                className="px-6 py-3 bg-white dark:bg-gray-700 border border-gray-300
                         dark:border-gray-600 text-gray-700 dark:text-gray-300
                         hover:bg-gray-50 dark:hover:bg-gray-600 rounded-lg font-medium
                         transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              {/* Delete button (admin only) */}
              {session?.user?.role === 'Admin' && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving}
                  className="ml-auto px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg
                           font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Deleting...' : 'Delete Post'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogEdit;
